/**
 * MarketingReportCharts.tsx
 *
 * Recharts charts for the marketing Reports page (email / SMS / push).
 * Palette follows the dataviz method: categorical slots in fixed order,
 * validated for CVD separation on the white card surface (worst adjacent
 * ΔE ≥ 21); sub-3:1 hues are relieved by the always-on legend, hover
 * tooltips, and the campaign tables under each chart. Text wears text
 * tokens (slate), never the series color.
 */
import { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

// Categorical slots (validated): 1 blue, 2 aqua, 3 yellow, 6 red, 7 violet.
// Violet is Leads on both charts — colour follows the entity. Re-validated with
// it in place: email blue/aqua/yellow/violet passes all-pairs; the SMS stack's
// one CVD warn (aqua↔red) predates it and is relieved by the stroke gaps.
const C = {
  blue: '#2a78d6',
  aqua: '#1baf7a',
  yellow: '#eda100',
  red: '#e34948',
  violet: '#4a3aa7',
};

const GRID = '#e2e8f0';       // slate-200 — recessive
const TICK = { fill: '#64748b', fontSize: 12 } as const; // slate-500

function shortDate(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime())
    ? d
    : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const tooltipProps = {
  contentStyle: {
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 12px rgb(2 6 23 / 0.08)',
    fontSize: 12,
  },
  // Text tokens, not series colors (the legend + marker carry identity).
  itemStyle: { color: '#334155' },
  labelStyle: { color: '#0f172a', fontWeight: 600 },
  labelFormatter: (label: string) => shortDate(String(label)),
} as const;

const legendProps = {
  formatter: (value: string) => <span className="text-xs text-slate-600">{value}</span>,
  iconSize: 10,
} as const;

/* ── Chart-type choice (SCRUM-1360) ─────────────────────────────────────── */

export type ChartKind = 'bar' | 'line' | 'area';

const CHART_KINDS: Array<{ value: ChartKind; label: string }> = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'area', label: 'Area Chart' },
];

/**
 * The reader's chart choice, remembered per browser.
 *
 * Defaults to BARS. These series are per-day counts — each point is one day's
 * total, not a sample of something continuous — and a line drawn between them
 * implies movement that was never measured. That is the complaint this came
 * from: email activity was a line while SMS delivery, right beside it on the
 * same page, was already bars.
 */
function useChartKind(storageKey: string): [ChartKind, (k: ChartKind) => void] {
  const [kind, setKind] = useState<ChartKind>(() => {
    try {
      const v = window.localStorage.getItem(storageKey);
      if (v === 'bar' || v === 'line' || v === 'area') return v;
    } catch { /* private mode — the default is fine */ }
    return 'bar';
  });
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, kind); } catch { /* not worth failing over */ }
  }, [kind, storageKey]);
  return [kind, setKind];
}

/** Matches the Reports page's own "Type:" select, which is where this was asked for. */
function ChartTypeSelect({ value, onChange }: { value: ChartKind; onChange: (k: ChartKind) => void }) {
  return (
    <div className="mb-1 flex justify-end">
      <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
        Type:
        <select
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:border-slate-900 focus:outline-none"
          value={value}
          onChange={(e) => onChange(e.target.value as ChartKind)}
        >
          {CHART_KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    </div>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div className="flex h-[280px] items-center justify-center text-sm text-slate-400">
      {label}
    </div>
  );
}

/* ── Email: sent / opens / clicks / leads over time ─────────────────────── */

export const EMAIL_SERIES = [
  { key: 'sent', name: 'Sent', color: C.blue },
  { key: 'opens', name: 'Opens', color: C.aqua },
  { key: 'clicks', name: 'Clicks', color: C.yellow },
  // Request-info leads, bucketed by the day the lead was created.
  { key: 'leads', name: 'Leads', color: C.violet },
] as const;

export function EmailActivityChart({ data }: {
  /** `leads` is absent from API builds that predate request-info links. */
  data: Array<{ date: string; sent: number; opens: number; clicks: number; leads?: number }>;
}) {
  const [kind, setKind] = useChartKind('marketing.reports.emailChartKind');
  if (!data.length) return <EmptyChart label="No email activity in this date range." />;
  // A missing lead count is drawn as zero, not as a gap in the series.
  const rows = data.map((d) => ({ ...d, leads: d.leads ?? 0 }));

  // Axes, tooltip and legend are shared across the three types, so switching
  // changes only the marks — the same numbers, not a differently-scaled picture.
  const frame = (
    <>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="date" tickFormatter={shortDate} tick={TICK} axisLine={false} tickLine={false} />
      <YAxis tick={TICK} axisLine={false} tickLine={false} allowDecimals={false} />
      <Tooltip {...tooltipProps} cursor={kind === 'bar' ? { fill: 'rgb(241 245 249 / 0.7)' } : { stroke: '#cbd5e1' }} />
      <Legend {...legendProps} />
    </>
  );

  return (
    <>
      <ChartTypeSelect value={kind} onChange={setKind} />
      <ResponsiveContainer width="100%" height={280}>
        {kind === 'bar' ? (
          // Grouped, NOT stacked: sent/opens/clicks/leads are nested subsets of
          // each other, so stacking them would draw a total that means nothing.
          // (SmsDeliveryChart stacks because its parts really do sum to sent.)
          <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -12 }} barCategoryGap="25%">
            {frame}
            {EMAIL_SERIES.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[3, 3, 0, 0]} maxBarSize={22} />
            ))}
          </BarChart>
        ) : kind === 'area' ? (
          <AreaChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            {frame}
            {EMAIL_SERIES.map((s) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                fill={s.color} fillOpacity={0.12} strokeWidth={2} activeDot={{ r: 4 }} />
            ))}
          </AreaChart>
        ) : (
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            {frame}
            {EMAIL_SERIES.map((s) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </>
  );
}

/* ── SMS: per-day delivery outcome (part-to-whole, stacked) ─────────────── */

export function SmsDeliveryChart({ data }: {
  /** `leads` is absent from API builds that predate request-info links. */
  data: Array<{ date: string; sent: number; delivered: number; failed: number; leads?: number }>;
}) {
  if (!data.length) return <EmptyChart label="No SMS activity in this date range." />;
  // Part-to-whole per day: delivered + failed + still-pending remainder.
  const rows = data.map((d) => ({
    ...d,
    pending: Math.max(d.sent - d.delivered - d.failed, 0),
    leads: d.leads ?? 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -12 }} barCategoryGap="35%">
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={TICK} axisLine={false} tickLine={false} />
        <YAxis tick={TICK} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip {...tooltipProps} cursor={{ fill: 'rgb(241 245 249 / 0.7)' }} />
        <Legend {...legendProps} />
        {/* 2px surface gap between stacked segments via white stroke. */}
        <Bar dataKey="delivered" name="Delivered" stackId="s" fill={C.aqua} stroke="#ffffff" strokeWidth={1} />
        <Bar dataKey="failed" name="Failed" stackId="s" fill={C.red} stroke="#ffffff" strokeWidth={1} />
        <Bar dataKey="pending" name="Pending" stackId="s" fill={C.yellow} stroke="#ffffff" strokeWidth={1} radius={[3, 3, 0, 0]} />
        {/* Leads are not a part of the delivery whole, so they stand BESIDE the
            stack on their own stackId. On top of it they would make each day's
            bar total something other than "sent". */}
        <Bar dataKey="leads" name="Leads" stackId="leads" fill={C.violet} stroke="#ffffff" strokeWidth={1} radius={[3, 3, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ── Push: sent / delivered / opened over time ──────────────────────────── */

export function PushActivityChart({ data }: {
  data: Array<{ date: string; sent: number; delivered: number; opened: number; failed: number }>;
}) {
  if (!data.length) return <EmptyChart label="No push activity in this date range." />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={TICK} axisLine={false} tickLine={false} />
        <YAxis tick={TICK} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip {...tooltipProps} cursor={{ stroke: '#cbd5e1' }} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="sent" name="Sent" stroke={C.blue} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
        <Line type="monotone" dataKey="delivered" name="Delivered" stroke={C.aqua} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
        <Line type="monotone" dataKey="opened" name="Opened" stroke={C.yellow} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}
